import { useRuntimeVariables } from 'app-kit'
import { useGeckoAuth } from 'features/auth'
import { usePipelineConfig } from 'features/knowledgeDocumentViewer/contexts/PipelineConfigContext'
import {
  fetchIntraDocSearchHits,
  type IntraDocSearchHit,
} from 'features/knowledgeDocumentViewer/hooks/useIntraDocumentSearch'
import type { FragmentHighlight } from 'features/knowledgeDocumentViewer/types'
import { extractContiguousMarkedPhrases } from 'features/knowledgeDocumentViewer/utils/textHelpers'
import { useEffect, useRef, useState } from 'react'

const MAX_PHRASE_ATTEMPTS = 3

export type FragmentHighlightState = {
  isActive: boolean
  isLoading: boolean
  hit: IntraDocSearchHit | null
}

async function findFirstPageHit(
  axios: Pick<import('axios').Axios, 'post'>,
  backendUrl: string,
  documentId: string,
  siteId: string,
  phrases: string[],
  pageNum: number,
  signal: AbortSignal,
  previewMode: boolean
): Promise<IntraDocSearchHit | null> {
  for (const phrase of phrases.slice(0, MAX_PHRASE_ATTEMPTS)) {
    if (signal.aborted) return null
    try {
      const hits = await fetchIntraDocSearchHits(
        axios,
        backendUrl,
        documentId,
        siteId,
        phrase,
        signal,
        200,
        previewMode
      )
      const pageHit = hits.find((h) => h.page_number === pageNum)
      if (pageHit) return pageHit
    } catch {
      if (signal.aborted) return null
    }
  }
  return null
}

export function useFragmentHighlight(
  documentId: string,
  siteId: string,
  fragmentHighlight: FragmentHighlight | undefined,
  intraSearchIsOpen: boolean
): FragmentHighlightState {
  const [hit, setHit] = useState<IntraDocSearchHit | null>(null)
  const [isActive, setIsActive] = useState(false)
  const [isLoading, setIsLoading] = useState(false)

  const { authorizedAxios: axios } = useGeckoAuth()
  const { isPreviewMode } = usePipelineConfig()
  const { POWER_BACKEND_URL } = useRuntimeVariables()

  const axiosRef = useRef(axios)
  axiosRef.current = axios
  const urlRef = useRef(POWER_BACKEND_URL)
  urlRef.current = POWER_BACKEND_URL

  const fragmentKey = fragmentHighlight
    ? `${documentId}:${siteId}:${fragmentHighlight.pageNum}:${fragmentHighlight.fragmentText}`
    : null

  useEffect(() => {
    if (intraSearchIsOpen) {
      setHit(null)
      setIsActive(false)
      setIsLoading(false)
    }
  }, [intraSearchIsOpen])

  useEffect(() => {
    if (!fragmentHighlight || !fragmentKey) {
      setHit(null)
      setIsActive(false)
      setIsLoading(false)
      return
    }

    // Fragment text may be marked-up snippet HTML (search results) or a plain
    // string (an anchor id / cited phrase from an evidence panel). Marked
    // phrases are the highest-precision needles; fall back to the raw text so
    // plain-string fragments highlight instead of silently no-oping.
    const marked = extractContiguousMarkedPhrases(
      fragmentHighlight.fragmentText
    )
    const plain = fragmentHighlight.fragmentText.trim()
    const phrases =
      marked.length > 0 ? marked : plain && !plain.includes('<') ? [plain] : []
    if (phrases.length === 0) return

    const controller = new AbortController()
    setIsActive(true)
    setIsLoading(true)

    findFirstPageHit(
      axiosRef.current,
      urlRef.current,
      documentId,
      siteId,
      phrases,
      fragmentHighlight.pageNum,
      controller.signal,
      isPreviewMode
    )
      .then((result) => {
        if (controller.signal.aborted) return
        setHit(result)
      })
      .catch(() => {
        if (controller.signal.aborted) return
        setHit(null)
      })
      .finally(() => {
        if (!controller.signal.aborted) {
          setIsLoading(false)
        }
      })

    return () => controller.abort()
  }, [fragmentKey, documentId, siteId, fragmentHighlight, isPreviewMode])

  return { isActive, isLoading, hit }
}
